import { format as formatDate } from "date-fns";
import { Immutable } from "immer";
import React from "react";
import { t } from "ttag";

import { useAppSelector } from "../../../store/reducers";
import { PredictedDeliveryLoader } from "../PredictedDeliveryLoader";
import {
    PredictedDeliveryDateStatus,
    PreferredDeliveryDateDisplayType,
} from "../constants";
import { IDeliveryDatePredictionState } from "../reducers.interfaces";

import styles from "./index.module.scss";

const NO_PREFERENCE = "";

const getSlots = (
    type: PreferredDeliveryDateDisplayType,
    prediction: IDeliveryDatePredictionState,
): Date[] => {
    if (prediction.status !== PredictedDeliveryDateStatus.LOADED) {
        return [];
    }
    const slots =
        type === PreferredDeliveryDateDisplayType.SHOW_AGGRESSIVE
            ? prediction.data.slots_aggressive.map((d) => new Date(d))
            : prediction.data.slots_conservative.map((d) => new Date(d));
    return slots;
};

const formatDateOptionLabel = (dt: Date): string => {
    return formatDate(dt, "EEEE, MMM do, yyyy");
};

const formatDateOptionValue = (dt: Date): string => {
    return dt.toISOString();
};

const setDefaultDeliveryDate = (
    type: PreferredDeliveryDateDisplayType,
    prediction: IDeliveryDatePredictionState,
    savePreferredDeliveryDate: (date: Date | null) => Promise<void>,
): void => {
    const slots = getSlots(type, prediction);
    if (slots.length > 0) {
        void savePreferredDeliveryDate(slots[0]);
    }
};

const getPreferredDeliveryText = (
    preferredDeliveryContent: React.ReactNode | undefined,
    preferredDate: Date | null,
): React.ReactNode => {
    if (preferredDeliveryContent) {
        return preferredDeliveryContent;
    } else if (!preferredDate) {
        // TODO(i18n): Wrap in t tagged template: t`Need a later date? A local delivery agent will call you to schedule your in-home delivery on a day that's convenient for you.`
        return "Need a later date? A local delivery agent will call you to schedule your in-home delivery on a day that's convenient for you.";
    }
    // TODO(i18n): Wrap in t tagged template: t`Please Note: We will do our best to accommodate your request. A local delivery agent will call you to confirm your delivery date and time.`
    return "Please Note: We will do our best to accommodate your request. A local delivery agent will call you to confirm your delivery date and time.";
};

const isPredictionDataReady = (
    type: PreferredDeliveryDateDisplayType,
    predictionStatus: PredictedDeliveryDateStatus,
): boolean => {
    return !(
        type === PreferredDeliveryDateDisplayType.DISABLED ||
        predictionStatus === PredictedDeliveryDateStatus.INITIAL ||
        predictionStatus === PredictedDeliveryDateStatus.ERROR ||
        predictionStatus === PredictedDeliveryDateStatus.LOADING
    );
};

const PDDOption = (props: Immutable<{ option: Date }>): React.JSX.Element => {
    const label = formatDateOptionLabel(props.option);
    const value = formatDateOptionValue(props.option);
    return <option value={value}>{label}</option>;
};

const PDDNullOption = (): React.JSX.Element => {
    return (
        <option value={NO_PREFERENCE}>{t`Select Another Delivery Date`}</option>
    );
};

const PDDOptionList = (
    props: Immutable<{
        type: PreferredDeliveryDateDisplayType;
        prediction: IDeliveryDatePredictionState;
    }>,
): React.JSX.Element => {
    const slots = getSlots(props.type, props.prediction);
    const options = slots.map((option) => (
        <PDDOption key={option.toISOString()} option={option} />
    ));
    options.push(<PDDNullOption key={NO_PREFERENCE} />);
    return <>{options}</>;
};

export const PreferredDeliveryDate = (
    props: Immutable<{
        type: PreferredDeliveryDateDisplayType;
        savePreferredDeliveryDate: (date: Date | null) => Promise<void>;
        fallbackDescription: string;
        preferredDeliveryContent?: React.ReactNode;
    }>,
): React.ReactNode => {
    const prediction = useAppSelector(
        (state) => state.checkout.data.predicted_delivery_date,
    );
    const preferredDate = useAppSelector((state) =>
        state.checkout.data.preferred_date
            ? new Date(state.checkout.data.preferred_date)
            : null,
    );

    const hasMountedRef = React.useRef(false);
    const prevStatusRef = React.useRef(prediction?.status);

    const onSelectChange = (
        e: React.FormEvent<HTMLSelectElement | HTMLInputElement>,
    ): void => {
        e.preventDefault();
        const val = e.currentTarget.value;
        const pref = val === NO_PREFERENCE ? null : new Date(val);
        void props.savePreferredDeliveryDate(pref);
    };

    React.useEffect(() => {
        const hasMounted = hasMountedRef.current;
        const prevStatus = prevStatusRef.current;
        const isDisabled =
            props.type === PreferredDeliveryDateDisplayType.DISABLED;
        const status = prediction?.status;

        const shouldSetDefaultOnMount =
            !hasMounted &&
            !isDisabled &&
            status === PredictedDeliveryDateStatus.LOADED;

        const shouldSetDefaultOnStatusLoad =
            hasMounted &&
            !isDisabled &&
            prevStatus === PredictedDeliveryDateStatus.LOADING &&
            status === PredictedDeliveryDateStatus.LOADED &&
            !preferredDate;

        if (
            prediction &&
            (shouldSetDefaultOnMount || shouldSetDefaultOnStatusLoad)
        ) {
            setDefaultDeliveryDate(
                props.type,
                prediction,
                props.savePreferredDeliveryDate,
            );
        }

        hasMountedRef.current = true;
        prevStatusRef.current = status;
    }, [
        prediction,
        props.type,
        props.savePreferredDeliveryDate,
        preferredDate,
    ]);

    const isPredictedDataReady = prediction
        ? isPredictionDataReady(props.type, prediction.status)
        : false;

    const preferredDeliveryText = getPreferredDeliveryText(
        props.preferredDeliveryContent,
        preferredDate,
    );

    return (
        <>
            <PredictedDeliveryLoader />
            {isPredictedDataReady && prediction ? (
                <div>
                    <label
                        className={styles.container}
                        htmlFor="preferred-delivery-date"
                    >
                        <span className={styles.label}>
                            {/* TODO(i18n): Wrap in t tagged template: {t`Preferred Delivery Date`} */}
                            Preferred Delivery Date
                        </span>
                        <select
                            id="preferred-delivery-date"
                            value={
                                preferredDate
                                    ? formatDateOptionValue(preferredDate)
                                    : NO_PREFERENCE
                            }
                            onChange={onSelectChange}
                            className={styles.select}
                        >
                            <PDDOptionList
                                type={props.type}
                                prediction={prediction}
                            />
                        </select>
                    </label>
                    {preferredDeliveryText}
                </div>
            ) : (
                <p>{props.fallbackDescription}</p>
            )}
        </>
    );
};
